// Contratos da API do piloto automático (fatia S3b). Dinheiro em REAIS.
import type { AdProvider, AutopilotMode, RuleId } from '../domain'

export interface RuleParamMeta { key: string; label: string; unit?: 'x' | '%' | 'R$' | 'h' | 'dias' | '' }

export interface RuleMeta {
  id: RuleId
  title: string
  description: string
  /** Copiloto pode aplicar sozinho? */
  copilot: 'yes' | 'no' | 'alert'
  params: Record<string, number>
  paramMeta: RuleParamMeta[]
}

/** Catálogo de regras v1 (seção 11.3 do plano técnico). Parâmetros padrão editáveis por empresa. */
export const RULE_CATALOG: Record<RuleId, RuleMeta> = {
  R1: {
    id: 'R1', title: 'Pausar anúncio ruim', copilot: 'yes',
    description: 'Pausa anúncios que gastam e não trazem clientes (nunca o último ativo do grupo).',
    params: { spendMultiple: 2, cprMultiple: 2, minImpressions: 1000 },
    paramMeta: [
      { key: 'spendMultiple', label: 'Gasto sem resultado (vezes a meta de custo)', unit: 'x' },
      { key: 'cprMultiple', label: 'Custo por cliente acima da mediana do grupo', unit: 'x' },
      { key: 'minImpressions', label: 'Mínimo de exibições', unit: '' },
    ],
  },
  R2: {
    id: 'R2', title: 'Escalar o que funciona', copilot: 'yes',
    description: 'Aumenta um pouco o orçamento de campanhas baratas que estão sem verba.',
    params: { maxCprRatio: 0.8, minBudgetUse: 0.9, minProbability: 0.9, increasePct: 15, minResults: 5, cooldownHours: 72 },
    paramMeta: [
      { key: 'maxCprRatio', label: 'Custo por cliente até (vezes a meta)', unit: 'x' },
      { key: 'minBudgetUse', label: 'Uso mínimo do orçamento', unit: 'x' },
      { key: 'increasePct', label: 'Aumento máximo', unit: '%' },
      { key: 'minResults', label: 'Mínimo de clientes em 7 dias', unit: '' },
      { key: 'cooldownHours', label: 'Espera depois de mudar', unit: 'h' },
    ],
  },
  R3: {
    id: 'R3', title: 'Reduzir o que está caro', copilot: 'yes',
    description: 'Diminui o orçamento de campanhas com custo por cliente bem acima da meta.',
    params: { minCprRatio: 1.5, decreasePct: 20, minSpendMultiple: 3, minImpressions: 1000 },
    paramMeta: [
      { key: 'minCprRatio', label: 'Custo por cliente a partir de (vezes a meta)', unit: 'x' },
      { key: 'decreasePct', label: 'Redução', unit: '%' },
      { key: 'minSpendMultiple', label: 'Gasto mínimo para decidir (vezes a meta)', unit: 'x' },
    ],
  },
  R4: {
    id: 'R4', title: 'Mover verba para o melhor', copilot: 'yes',
    description: 'Tira dinheiro de uma campanha cara e coloca numa barata com o mesmo objetivo, sem mudar o total.',
    params: { minProbability: 0.9, maxMovePct: 20, minResults: 5 },
    paramMeta: [
      { key: 'maxMovePct', label: 'Máximo movido da campanha de origem', unit: '%' },
      { key: 'minResults', label: 'Mínimo de clientes em 7 dias', unit: '' },
    ],
  },
  R5: {
    id: 'R5', title: 'Anunciar só no horário útil', copilot: 'no',
    description: 'Sugere desligar anúncios nas horas em que você gasta e não recebe contatos.',
    params: { minClicks: 20, minWasteReais: 20 },
    paramMeta: [
      { key: 'minClicks', label: 'Cliques mínimos na hora', unit: '' },
      { key: 'minWasteReais', label: 'Gasto desperdiçado mínimo (28 dias)', unit: 'R$' },
    ],
  },
  R6: {
    id: 'R6', title: 'Bloquear buscas sem intenção de compra', copilot: 'yes',
    description: 'Bloqueia pesquisas como "receita", "grátis" e "curso", que gastam sem vender.',
    params: { minSpendReais: 10 },
    paramMeta: [{ key: 'minSpendReais', label: 'Gasto mínimo do termo (28 dias)', unit: 'R$' }],
  },
  R7: {
    id: 'R7', title: 'Avisar quando o anúncio cansar', copilot: 'no',
    description: 'Quando as pessoas já viram muito o mesmo anúncio, sugerimos criar versões novas.',
    params: { maxFrequency: 4, ctrDropPct: 30, minImpressions: 1000 },
    paramMeta: [
      { key: 'maxFrequency', label: 'Vezes que cada pessoa viu (7 dias)', unit: 'x' },
      { key: 'ctrDropPct', label: 'Queda nos cliques', unit: '%' },
    ],
  },
  R8: {
    id: 'R8', title: 'Segurar o ritmo do orçamento', copilot: 'yes',
    description: 'Se o mês vai passar do teto, reduz os orçamentos na mesma proporção. É uma proteção.',
    params: { marginPct: 0 },
    paramMeta: [{ key: 'marginPct', label: 'Margem de segurança', unit: '%' }],
  },
  R9: {
    id: 'R9', title: 'Avisar se a medição quebrar', copilot: 'alert',
    description: 'Se os contatos param de ser contados, avisamos e congelamos aumentos.',
    params: { silentHours: 72, minPrevResults: 5 },
    paramMeta: [
      { key: 'silentHours', label: 'Horas sem nenhum contato', unit: 'h' },
      { key: 'minPrevResults', label: 'Contatos antes (14 dias)', unit: '' },
    ],
  },
  R10: {
    id: 'R10', title: 'Detectar gasto fora do normal', copilot: 'alert',
    description: 'Se o gasto do dia ou o custo por clique dispara, avisamos e pausamos o piloto.',
    params: { spendMultiple: 1.5, cpcMultiple: 3, minClicks: 20 },
    paramMeta: [
      { key: 'spendMultiple', label: 'Gasto do dia (vezes o orçamento diário)', unit: 'x' },
      { key: 'cpcMultiple', label: 'Custo por clique (vezes a mediana)', unit: 'x' },
    ],
  },
  R11: {
    id: 'R11', title: 'Respeitar o aprendizado', copilot: 'alert',
    description: 'Enquanto a plataforma está aprendendo, não mexemos na campanha.',
    params: {},
    paramMeta: [],
  },
  R12: {
    id: 'R12', title: 'Lembrar datas comerciais', copilot: 'no',
    description: 'Prepara um rascunho de campanha para datas como Dia das Mães e Black Friday.',
    params: { minDays: 10, maxDays: 21 },
    paramMeta: [
      { key: 'minDays', label: 'Antecedência mínima', unit: 'dias' },
      { key: 'maxDays', label: 'Antecedência máxima', unit: 'dias' },
    ],
  },
  R13: {
    id: 'R13', title: 'Avisar quando o clique não vira visita', copilot: 'no',
    description: 'Se há muito clique no anúncio e quase nenhuma visita no site, avisamos para você conferir o link, a velocidade da página e a tag. Só sugere: não mexe em nada.',
    params: { minClicks: 100, minVisitPct: 30, silentDays: 7 },
    paramMeta: [
      { key: 'minClicks', label: 'Cliques mínimos em 7 dias', unit: '' },
      { key: 'minVisitPct', label: 'Visitas esperadas por clique', unit: '%' },
      { key: 'silentDays', label: 'Dias sem sinal da tag', unit: 'dias' },
    ],
  },
}

export interface RuleStateDto {
  id: RuleId
  enabled: boolean
  shadow: boolean
  params: Record<string, number>
  /** Desligada/sombra globalmente pela equipe do Impulso */
  globallyDisabled: boolean
  globallyShadow: boolean
}

/** GET/PUT /api/v1/companies/:companyId/autopilot */
export interface AutopilotPolicyDto {
  mode: AutopilotMode
  /** Modos permitidos pelo plano */
  allowedModes: AutopilotMode[]
  monthlyCap: number | null
  targetCostPerResult: number | null
  maxDailyChangePct: number
  requireApprovalTotalIncrease: boolean
  quietHours: { start: string; end: string } | null
  protectedCampaignIds: string[]
  rules: RuleStateDto[]
  paused: boolean
  pausedReason: string | null
  updatedAt: string | null
}

export interface UpdateAutopilotPolicyBody {
  mode?: AutopilotMode
  monthlyCap?: number | null
  targetCostPerResult?: number | null
  maxDailyChangePct?: number
  requireApprovalTotalIncrease?: boolean
  quietHours?: { start: string; end: string } | null
  protectedCampaignIds?: string[]
  rules?: Partial<Record<RuleId, { enabled?: boolean; shadow?: boolean; params?: Record<string, number> }>>
}

export interface AutopilotRunLogEntry {
  ruleId: RuleId
  title: string
  outcome: 'executed' | 'recommended' | 'blocked' | 'shadow' | 'conflict' | 'alert'
  reason?: string
  code?: string
  entityIds?: string[]
  budgetDelta?: number
}

export interface AutopilotRunDto {
  id: string
  trigger: 'metrics_updated' | 'daily' | 'manual' | 'simulation'
  mode: AutopilotMode
  proposals: number
  executed: number
  recommended: number
  blocked: number
  startedAt: string
  finishedAt: string | null
  log: AutopilotRunLogEntry[]
  notes: string[]
}

export interface AutopilotStatusDto {
  mode: AutopilotMode
  effectiveMode: AutopilotMode
  paused: boolean
  pausedReason: string | null
  killSwitch: boolean
  leases: Array<{ provider: AdProvider | string; externalAccountId: string; heldByThisCompany: boolean }>
  lastRuns: AutopilotRunDto[]
  /** Impacto acumulado das ações do piloto (medido após 7 dias) */
  /** `extraResults` pode ser negativo; `savings` só conta quando os resultados não caíram; `extraSpend` é o gasto a mais medido */
  impact: { actions: number; measured: number; extraResults: number; savings: number; extraSpend: number }
}

/** POST .../autopilot/simulate */
export interface SimulateBody { mode: AutopilotMode; days: number }

export interface SimulatedItem { ruleId: RuleId; title: string; reason?: string; budgetDelta?: number }

export interface SimulationDay {
  date: string
  executed: SimulatedItem[]
  recommended: SimulatedItem[]
  blocked: SimulatedItem[]
}

export interface SimulationResultDto {
  mode: AutopilotMode
  from: string
  to: string
  days: SimulationDay[]
  summary: { executed: number; recommended: number; blocked: number; budgetChange: number }
  checks: { guardrailsOk: boolean; violations: Array<{ date: string; ruleId: RuleId; problem: string }> }
  assumptions: string[]
}
